/**
 * Resilience Module
 *
 * Exports circuit breaker and retry utilities for building resilient services.
 *
 * Clean Architecture:
 * - Infrastructure concerns at system boundaries
 * - Services use these utilities to protect external operations
 * - Domain and use-case layers remain unaware of resilience mechanisms
 *
 * Usage:
 * ```ts
 * import { getCircuitBreaker, retryWithBackoff } from '@/app/services/resilience';
 *
 * // Get or create a named circuit breaker
 * const cb = getCircuitBreaker('my-api');
 *
 * // Execute with circuit breaker protection
 * const result = await cb.execute(() => fetchData());
 *
 * // Or combine with retry
 * const result = await retryWithBackoff(
 *   () => cb.execute(() => fetchData()),
 *   { maxRetries: 3 }
 * );
 * ```
 */

import { Logger } from '@/app/utils/logger';
import { CircuitBreakerService, CircuitState, CircuitOpenError } from './CircuitBreakerService';
import type { CircuitBreakerConfig } from './CircuitBreakerService';
import {
  retryWithBackoff,
  retryWithBackoffResult,
  createRetryPolicy,
  RetryAbortedError,
  isRetryableHttpError,
} from './RetryPolicy';
import type { RetryOptions, RetryResult } from './RetryPolicy';

const LOG_TAG = 'resilience';

// Re-export all types and classes
export {
  CircuitBreakerService,
  CircuitState,
  CircuitOpenError,
  retryWithBackoff,
  retryWithBackoffResult,
  createRetryPolicy,
  RetryAbortedError,
  isRetryableHttpError,
};
export type { CircuitBreakerConfig, RetryOptions, RetryResult };

/**
 * Registry of named circuit breakers
 * Ensures single instance per name across the application
 */
const circuitBreakerRegistry = new Map<string, CircuitBreakerService>();

/**
 * Default configurations for known services
 */
const DEFAULT_CONFIGS: Record<string, Partial<CircuitBreakerConfig>> = {
  'r2-playlist': {
    failureThreshold: 3,
    resetTimeout: 30000, // 30 seconds
    successThreshold: 2,
  },
  'atc-manifest': {
    failureThreshold: 3,
    resetTimeout: 60000, // 1 minute (ATC data doesn't change often)
    successThreshold: 2,
  },
  'atc-station': {
    failureThreshold: 3,
    resetTimeout: 30000, // 30 seconds
    successThreshold: 2,
  },
};

/**
 * Global state change handler for logging
 */
function defaultStateChangeHandler(
  name: string,
  from: CircuitState,
  to: CircuitState
): void {
  if (to === CircuitState.OPEN) {
    Logger.warn(`Circuit breaker "${name}" opened (${from} → ${to})`, LOG_TAG);
  } else if (to === CircuitState.CLOSED) {
    Logger.info(`Circuit breaker "${name}" closed (${from} → ${to})`, LOG_TAG);
  } else {
    Logger.info(`Circuit breaker "${name}" state change: ${from} → ${to}`, LOG_TAG);
  }
}

/**
 * Get or create a named circuit breaker
 *
 * Uses default configuration for known services (r2-playlist, atc-manifest, atc-station)
 * Falls back to sensible defaults for unknown services.
 *
 * @param name - Unique name for the circuit breaker
 * @param configOverrides - Optional configuration overrides
 * @returns CircuitBreakerService instance
 *
 * @example
 * ```ts
 * const cb = getCircuitBreaker('r2-playlist');
 * const result = await cb.execute(() => fetchPlaylist());
 * ```
 */
export function getCircuitBreaker(
  name: string,
  configOverrides?: Partial<CircuitBreakerConfig>
): CircuitBreakerService {
  // Return existing instance if available
  if (circuitBreakerRegistry.has(name)) {
    const existing = circuitBreakerRegistry.get(name)!;
    Logger.debug(`Returning existing circuit breaker "${name}"`, LOG_TAG);
    return existing;
  }

  // Get default config for known services
  const defaultConfig = DEFAULT_CONFIGS[name] || {};

  // Create new circuit breaker
  const config: CircuitBreakerConfig = {
    name,
    ...defaultConfig,
    ...configOverrides,
    onStateChange: configOverrides?.onStateChange || defaultStateChangeHandler,
  };

  const circuitBreaker = new CircuitBreakerService(config);
  circuitBreakerRegistry.set(name, circuitBreaker);

  Logger.info(`Created circuit breaker "${name}"`, LOG_TAG);
  return circuitBreaker;
}

/**
 * Create a new circuit breaker without registering it
 * Useful for testing or isolated use cases
 *
 * @param config - Full configuration for the circuit breaker
 * @returns New CircuitBreakerService instance
 */
export function createCircuitBreaker(config: CircuitBreakerConfig): CircuitBreakerService {
  return new CircuitBreakerService(config);
}

/**
 * Get all registered circuit breakers
 * Useful for monitoring and debugging
 */
export function getAllCircuitBreakers(): Map<string, CircuitBreakerService> {
  return new Map(circuitBreakerRegistry);
}

/**
 * Get stats for all registered circuit breakers
 */
export function getAllCircuitBreakerStats(): Array<{
  name: string;
  state: CircuitState;
  failures: number;
  successes: number;
  failureThreshold: number;
  successThreshold: number;
  timeUntilRetry: number | null;
}> {
  return Array.from(circuitBreakerRegistry.values()).map((cb) => cb.getStats());
}

/**
 * Reset all circuit breakers
 * Useful for testing or after recovering from system-wide outages
 */
export function resetAllCircuitBreakers(): void {
  Logger.info(`Resetting all ${circuitBreakerRegistry.size} circuit breakers`, LOG_TAG);
  circuitBreakerRegistry.forEach((cb) => cb.reset());
}

/**
 * Clear the circuit breaker registry
 * Primarily for testing purposes
 */
export function clearCircuitBreakerRegistry(): void {
  Logger.debug('Clearing circuit breaker registry', LOG_TAG);
  circuitBreakerRegistry.clear();
}

/**
 * Check if a specific circuit breaker is open
 *
 * @param name - Name of the circuit breaker
 * @returns true if circuit is open, false if closed/half-open or not found
 */
export function isCircuitOpen(name: string): boolean {
  const cb = circuitBreakerRegistry.get(name);
  return cb?.isOpen() ?? false;
}

/**
 * Pre-configured retry policies for common scenarios
 */
export const retryPolicies = {
  /**
   * Default retry policy with exponential backoff
   */
  default: createRetryPolicy({
    maxRetries: 3,
    baseDelay: 1000,
    maxDelay: 10000,
  }),

  /**
   * Aggressive retry for critical operations
   */
  aggressive: createRetryPolicy({
    maxRetries: 5,
    baseDelay: 500,
    maxDelay: 15000,
  }),

  /**
   * Conservative retry for less critical operations
   */
  conservative: createRetryPolicy({
    maxRetries: 2,
    baseDelay: 2000,
    maxDelay: 10000,
  }),

  /**
   * Fast retries for operations that should recover quickly
   */
  fast: createRetryPolicy({
    maxRetries: 3,
    baseDelay: 250,
    maxDelay: 2000,
  }),
};
